import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  forwardRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { _HttpClient } from '@delon/theme';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';

@Component({
  selector: 'app-project-filter-select',
  templateUrl: './project-filter-select.component.html',
  styleUrl: './project-filter-select.component.less',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzSelectModule,
    NzSpinModule,
    NzDividerModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProjectFilterSelectComponent),
      multi: true
    }
  ]
})
export class ProjectFilterSelectComponent implements OnInit, OnChanges {
  @Input() selected: any[] = [];
  @Input() projectOpt: number = 0;
  sub: any = [];
  main: any = [];
  searching: boolean = false;
  @Output() readonly selectedChange = new EventEmitter();

  value: any[] = [];
  nodes: any[] = [];
  private isInternalChange: boolean = false;

  constructor(private http: _HttpClient) {}

  ngOnChanges(changes: SimpleChanges): void {
    if(!!changes['selected'] && !changes['selected'].firstChange && !this.isInternalChange){
      // Only update value if selected changes externally (not from our own onChange)
      // For Main Project, we need to convert child IDs back to parent IDs for display
      if (this.projectOpt == 2 && this.nodes.length > 0) {
        // Convert child IDs back to parent IDs for display
        const parentIds = this.getParentIds(changes['selected'].currentValue);
        this.value = parentIds;
      } else {
        this.value = changes['selected'].currentValue || [];
      }
    }
    this.isInternalChange = false;
  }

  ngOnInit(): void {
    this.value = this.selected || [];
    this.projectree();
    this.loadOption();
  }

  projectree() {
    this.http.get(`api/projects/tree`).subscribe({
      next: (res: any) => {
        this.nodes = res;
        // If we have selected values but value is empty, sync them
        if (this.selected && this.selected.length > 0 && (!this.value || this.value.length === 0)) {
          if (this.projectOpt == 2 && this.nodes.length > 0) {
            const parentIds = this.getParentIds(this.selected);
            this.value = parentIds;
          } else {
            this.value = this.selected;
          }
        }
      }
    });
  }

  loadOption() {
    this.searching = true;
    this.http.get('api/projects/allWithUserDefaults').subscribe({
      next: (res: any) => {
        this.sub = res.filter((o: any) => o.isActive && !o.hasChildren);
        this.main = res.filter((o: any) => o.isActive && o.hasChildren);
        this.searching = false;
      }
    });
  }

  onChange($event: any[]): void {
    // Always keep value in sync with what user selected (for UI display)
    this.value = $event || [];
    this.isInternalChange = true;
    
    if (this.projectOpt == 2) {
      // For Main Project, convert parent IDs to child IDs
      if (this.nodes && this.nodes.length > 0) {
        const childIds = this.getChildren($event);
        this.selected = childIds.length > 0 ? childIds : $event;
      } else {
        // If nodes not loaded yet, use parent IDs temporarily
        // The conversion will happen when nodes load
        this.selected = $event;
      }
    } else {
      this.selected = $event;
    }

    this.selectedChange.emit(this.selected);
  }

  getChildren(ids: any[], parentNode: any = null): any[] {
    let result: any[] = [];

    ids.forEach(id => {
      const item = this.findNodeById(id);
      if (item && item.children && item.children.length > 0) {
        const childrenIds = item.children.map((x: any) => x.id);
        result.push(...this.getChildren(childrenIds, item));
      } else if (item) {
        result.push(item.id);
      }
    });

    return result;
  }

  // Helper function to find a node by id in the entire tree
  findNodeById(id: any) {
    const queue = [...this.nodes];
    while (queue.length) {
      const node = queue.shift();
      if (node.id === id) return node;
      if (node.children) queue.push(...node.children);
    }
    return null;
  }

  // Helper function to convert child IDs back to parent IDs for display
  getParentIds(childIds: any[]): any[] {
    const parentIds = new Set<any>();
    
    childIds.forEach(childId => {
      const parent = this.findParentById(childId);
      if (parent) {
        parentIds.add(parent.id);
      }
    });
    
    return Array.from(parentIds);
  }

  // Helper function to find parent of a child node
  findParentById(childId: any): any {
    const queue = [...this.nodes];
    while (queue.length) {
      const node = queue.shift();
      if (node.children) {
        const child = node.children.find((c: any) => c.id === childId);
        if (child) {
          return node;
        }
        queue.push(...node.children);
      }
    }
    return null;
  }
}
